/**
 * Admin API Client
 * Centralized admin fetch calls with auth, safe parsing, and friendly errors
 */

const API_BASE = '/api';

/**
 * Get auth headers for admin requests
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
};

/**
 * Safe JSON parse that handles non-JSON error bodies
 */
const safeParseJSON = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { message: text || 'Unknown error' };
  }
};

/**
 * Convert HTTP status codes to user-friendly error messages
 */
const getErrorMessage = (status, data) => {
  switch (status) {
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You do not have permission to access this resource.';
    case 404:
      return data?.detail || 'The requested resource was not found.';
    case 422:
      return data?.detail || 'Invalid request data.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return data?.detail || data?.message || `Request failed (${status})`;
  }
};

/**
 * Admin API result type
 * @typedef {Object} AdminApiResult
 * @property {boolean} ok - Whether the request succeeded
 * @property {any} data - Response data (if ok)
 * @property {string|null} error - Error message (if not ok)
 * @property {number} status - HTTP status code
 * @property {boolean} isAuthError - Whether this is an auth error (401/403)
 */

/**
 * Make an admin API request
 * @param {string} endpoint - API endpoint (e.g., '/admin/users')
 * @param {Object} options - Fetch options
 * @returns {Promise<AdminApiResult>}
 */
export const adminFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE}${endpoint}`;
  
  const config = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  };

  try {
    const response = await fetch(url, config);
    const data = await safeParseJSON(response);
    
    if (response.ok) {
      return {
        ok: true,
        data,
        error: null,
        status: response.status,
        isAuthError: false,
      };
    }

    const isAuthError = response.status === 401 || response.status === 403;
    const errorMessage = getErrorMessage(response.status, data);

    return {
      ok: false,
      data: null,
      error: errorMessage,
      status: response.status,
      isAuthError,
    };
  } catch (err) {
    // Network error or other fetch failure
    return {
      ok: false,
      data: null,
      error: err.message || 'Network error. Please check your connection.',
      status: 0,
      isAuthError: false,
    };
  }
};

/**
 * Admin API GET request
 */
export const adminGet = (endpoint, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  
  const queryString = searchParams.toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  
  return adminFetch(url, { method: 'GET' });
};

/**
 * Admin API POST request
 */
export const adminPost = (endpoint, data = {}) => {
  return adminFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Admin API PUT request
 */
export const adminPut = (endpoint, data = {}) => {
  return adminFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Admin API PATCH request
 */
export const adminPatch = (endpoint, data = {}) => {
  return adminFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Admin API DELETE request
 */
export const adminDelete = (endpoint) => {
  return adminFetch(endpoint, { method: 'DELETE' });
};

/**
 * Resource endpoints configuration
 */
export const ADMIN_ENDPOINTS = {
  users: '/admin/users',
  creators: '/admin/creators',
  series: '/admin/series-list',
  artworks: '/admin/artworks',
  novels: '/admin/novels-list',
  reports: '/admin/reports',
  verifications: '/admin/verifications',
  notifications: '/admin/notifications',
  stats: '/admin/stats',
  alertSettings: '/admin/alert-settings',
  siteConfig: '/admin/site-config',
  featuredCreators: '/admin/featured-creators-list',
  featuredArtworks: '/admin/featured-artworks-list',
  auditLogs: '/admin/audit-logs',
};

export default {
  adminFetch,
  adminGet,
  adminPost,
  adminPut,
  adminPatch,
  adminDelete,
  getAuthHeaders,
  ADMIN_ENDPOINTS,
};
